/**
 * 🎯 Deal Match Score Component
 * Shows how well a deal matches buyer preferences (0-100)
 */

import React, { useState, useEffect } from 'react';

const DealMatchScore = ({ score, reasons, strength, showDetails = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get color based on score
  const getScoreColor = (score) => {
    if (score >= 85) return { bg: 'from-green-500 to-emerald-500', text: 'text-green-400' };
    if (score >= 70) return { bg: 'from-blue-500 to-cyan-500', text: 'text-blue-400' };
    if (score >= 55) return { bg: 'from-yellow-500 to-orange-500', text: 'text-yellow-400' };
    return { bg: 'from-gray-500 to-gray-400', text: 'text-gray-400' };
  };

  // Get strength label
  const getStrengthLabel = (strength) => {
    const labels = {
      perfect: { text: '🔥 TÖKÉLETES', color: 'text-red-400' },
      strong: { text: '✨ Erős', color: 'text-blue-400' },
      good: { text: '👍 Jó', color: 'text-green-400' },
      moderate: { text: '🤔 Közepes', color: 'text-yellow-400' },
      weak: { text: '❌ Gyenge', color: 'text-gray-400' }
    };
    return labels[strength] || labels.weak;
  };

  const colors = getScoreColor(score);
  const strengthLabel = getStrengthLabel(strength);

  return (
    <div className="relative">
      {/* Score Circle */}
      <div 
        className="inline-flex items-center gap-3 cursor-pointer group"
        onClick={() => showDetails && setIsExpanded(!isExpanded)}
      >
        {/* Circular Progress */}
        <div className="relative w-14 h-14">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="28"
              cy="28"
              r="24"
              stroke="currentColor"
              strokeWidth="4"
              fill="transparent"
              className="text-gray-700"
            />
            <circle
              cx="28"
              cy="28"
              r="24"
              stroke="currentColor"
              strokeWidth="4"
              fill="transparent"
              strokeDasharray={2 * Math.PI * 24}
              strokeDashoffset={2 * Math.PI * 24 * (1 - score / 100)}
              className={`bg-gradient-to-r ${colors.bg}`}
              style={{ 
                stroke: score >= 85 ? '#10b981' : score >= 70 ? '#3b82f6' : score >= 55 ? '#f59e0b' : '#6b7280'
              }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-sm font-black ${colors.text}`}>
              {score}%
            </span>
          </div>
        </div>

        {/* Strength Label */}
        <div className="flex flex-col">
          <span className={`text-xs font-bold ${strengthLabel.color}`}>
            {strengthLabel.text}
          </span>
          <span className="text-xs text-gray-400">
            Match Score
          </span>
        </div>
      </div>

      {/* Details Dropdown */}
      {showDetails && isExpanded && reasons && (
        <div className="absolute top-full left-0 mt-2 z-50 w-64">
          <div className="bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl p-4 shadow-2xl">
            <div className="text-xs text-gray-400 mb-3 uppercase tracking-wider">
              Miért passzol?
            </div>
            <ul className="space-y-2">
              {reasons.map((reason, idx) => (
                <li key={idx} className="text-sm text-white flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">✓</span>
                  {reason}
                </li>
              ))}
            </ul>
            {score >= 70 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="text-xs text-yellow-400 font-semibold">
                  💡 Ez a deal Neked szól!
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealMatchScore;
